// Component Library
import { FsxTableRowStatus } from 'fsx-component-library';

// Data Contracts
import type { CaseParticipant, CaseParticipantInfo, ParticipantRepresentation, Party, PersonName, Representation } from '@data-contracts';

// Local
import type { SendingPartiesPartyRow } from '@app/types/sending-parties-party-row.model';
import type { PartyRow } from '@shared/components/parties-table/parties-table.interfaces';

/**
 * Returns a sort key for a party row (party name) for alphabetical ordering.
 */
export function partyRowSortKey(row: PartyRow): string {
  const p = row.partyName;
  if (!p) return '';
  const full = p.fullName?.trim();
  if (full) return full;
  const last = (p.lastName ?? '').trim();
  const first = (p.firstName ?? '').trim();
  return `${last}, ${first}`.trim() || '';
}

/**
 * Sort key for a CaseParticipant by party name — matches {@link partyRowSortKey} for rows built from the same party.
 */
export function caseParticipantPartySortKey(cp: CaseParticipant): string {
  const pn = partyToPersonName(cp.party);
  if (!pn) return '';
  const full = pn.fullName?.trim();
  if (full) return full;
  const last = (pn.lastName ?? '').trim();
  const first = (pn.firstName ?? '').trim();
  return `${last}, ${first}`.trim() || '';
}

/**
 * Builds a PersonName from a single display name string (e.g. API returns one string).
 *
 * @param displayName - The display name string to convert to a PersonName.
 *
 * @returns A PersonName object.
 */
function toPersonName(displayName: string | null | undefined): PersonName | undefined {
  if (displayName == null || displayName === '') {
    return undefined;
  }
  return { fullName: displayName, lastName: displayName };
}

/**
 * Builds a PersonName from a Party (Filing API CaseParticipant.party).
 */
function partyToPersonName(party: Party | undefined): PersonName | undefined {
  if (!party) return undefined;
  const full =
    party.lexicalOrderName ??
    [party.firstName, party.middleName, party.lastName].filter(Boolean).join(' ').trim();
  return toPersonName(full || undefined);
}

/**
 * Reads attorneyId from a Representation; .NET API often returns PascalCase "AttorneyId".
 */
function getRepresentationAttorneyId(rep: Representation): string | null | undefined {
  const r = rep as Representation & { AttorneyId?: string | null };
  return r.attorneyId ?? r.AttorneyId ?? undefined;
}

/**
 * Maps a single Representation (Filing API CaseParticipant.attorneys item) to ParticipantRepresentation.
 */
function representationToParticipantRepresentation(rep: Representation): ParticipantRepresentation {
  return {
    name: rep.attorneyName ?? undefined,
    typeId: rep.attorneyTypeId ?? undefined,
    type: undefined,
    organizationId: rep.organizationId ?? undefined,
    organizationName: rep.attorneyOrganizationName ?? undefined,
  };
}

/**
 * Builds one PartyRow per attorney in a CaseParticipant (Filing API sending parties shape).
 * One row per party+attorney; if attorneys is empty, returns one party-only row.
 *
 * @param cp - The CaseParticipant from GET /filing/drafts/{filingId}/sendingparties.
 * @param startIndex - The index to use for the first row.
 * @returns Array of PartyRow.
 */
export function caseParticipantToPartyRowsByAttorney(
  cp: CaseParticipant,
  startIndex: number
): PartyRow[] {
  const party = cp.party;
  const attorneys = cp.attorneys ?? [];
  const partyBase: Omit<PartyRow, 'attorney' | 'attorneyType' | 'counselType' | 'counselTypeId' | 'firm' | 'orgId' | 'representations'> = {
    fsxTableRowStatus: FsxTableRowStatus.None,
    index: startIndex,
    partyId: party?.partyId ?? undefined,
    tempPartyId: party?.tempPartyId ?? undefined,
    partyName: partyToPersonName(party),
    partyType: undefined,
    partyTypeId: party?.partyTypeId,
    caseParticipantUniqueId: cp.caseParticipantUniqueId,
    role: party?.role ?? undefined,
  };

  if (attorneys.length === 0) {
    return [{ ...partyBase, index: startIndex }];
  }

  return attorneys.map((attorney: Representation, i: number) => {
    const rep = representationToParticipantRepresentation(attorney);
    return {
      ...partyBase,
      index: startIndex + i,
      attorney: toPersonName(attorney.attorneyName ?? undefined),
      attorneyUserGuid: attorney.attorneyId ?? undefined,
      attorneyType: undefined as string | null | undefined,
      attorneyTypeId: attorney.attorneyTypeId ?? undefined,
      counselType: undefined as string | undefined,
      counselTypeId: attorney.counselTypeId ?? undefined,
      firm: attorney.attorneyOrganizationName ?? undefined,
      orgId: attorney.organizationId != null ? String(attorney.organizationId) : undefined,
      representations: [rep],
    };
  });
}

/**
 * Converts CaseParticipant[] (Filing API sending parties) to PartyRow[] (one row per party+attorney).
 *
 * @param participants - The CaseParticipant array from GET /filing/drafts/{filingId}/sendingparties.
 * @returns Flat array of PartyRow with contiguous indices.
 */
export function sendingPartiesToPartyRows(participants: CaseParticipant[]): PartyRow[] {
  let index = 0;
  return participants.flatMap((cp) => {
    const rows = caseParticipantToPartyRowsByAttorney(cp, index);
    index += rows.length;
    return rows;
  });
}

/**
 * Deep-clones an object for round-trip storage so the original is not mutated.
 */
function deepClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value ?? undefined)) as T;
}

/**
 * Maps one CaseParticipant to PartyRow[] (one row per attorney), storing snapshots of Party and
 * Representation on each row so mapping back (newMapPartyRowToCaseParticipant / newMapPartyRowsToCaseParticipants)
 * reproduces the same CaseParticipant shape without loss.
 *
 * @param cp - The CaseParticipant from GET /filing/drafts/{filingId}/sendingparties.
 * @param startIndex - The index to use for the first row.
 * @returns Array of PartyRow with partySnapshot and representationSnapshot set.
 */
export function newMapCaseParticipantToPartyRow(
  cp: CaseParticipant,
  startIndex: number
): PartyRow[] {
  const party = cp.party;
  const attorneys = cp.attorneys ?? [];
  const partySnapshot = party ? deepClone(party) : undefined;

  const partyBase: Omit<PartyRow, 'attorney' | 'attorneyType' | 'counselType' | 'counselTypeId' | 'firm' | 'orgId' | 'representations' | 'partySnapshot' | 'representationSnapshot'> = {
    fsxTableRowStatus: FsxTableRowStatus.None,
    index: startIndex,
    partyId: party?.partyId ?? undefined,
    tempPartyId: party?.tempPartyId ?? undefined,
    partyName: partyToPersonName(party),
    partyType: cp.party.partyType ?? undefined,
    partyTypeId: party?.partyTypeId,
    caseParticipantUniqueId: cp.caseParticipantUniqueId,
    role: party?.role ?? undefined,
  };

  if (attorneys.length === 0) {
    return [{ ...partyBase, index: startIndex, partySnapshot }];
  }

  return attorneys.map((attorney: Representation, i: number) => {
    const rep = representationToParticipantRepresentation(attorney);
    const attorneyIdValue = getRepresentationAttorneyId(attorney);
    // Preserve attorneyId explicitly (API may use PascalCase "AttorneyId"; clone can lose it).
    const representationSnapshot: Representation = {
      ...deepClone(attorney),
      attorneyId: attorneyIdValue ?? null,
    };
    return {
      ...partyBase,
      index: startIndex + i,
      partySnapshot,
      representationSnapshot,
      attorney: toPersonName(attorney.attorneyName ?? undefined),
      attorneyUserGuid: attorneyIdValue ?? undefined,
      attorneyType: attorney.attorneyType ?? undefined as string | null | undefined,
      attorneyTypeId: attorney.attorneyTypeId ?? undefined,
      counselType: undefined as string | undefined,
      counselTypeId: attorney.counselTypeId ?? undefined,
      serviceMethodId: attorney.serviceMethodId ?? null,
      firm: attorney.attorneyOrganizationName ?? undefined,
      orgId: attorney.organizationId != null ? String(attorney.organizationId) : undefined,
      representations: [rep],
    };
  });
}

/**
 * Maps CaseParticipant[] to PartyRow[] (one row per party+attorney) using newMapCaseParticipantToPartyRow
 * so each row carries snapshots for lossless round-trip back to CaseParticipant.
 *
 * @param participants - The CaseParticipant array from GET /filing/drafts/{filingId}/sendingparties.
 * @returns Flat array of PartyRow with contiguous indices.
 */
export function newMapCaseParticipantsToPartyRows(participants: CaseParticipant[]): PartyRow[] {
  let index = 0;
  return participants.flatMap((cp) => {
    const rows = newMapCaseParticipantToPartyRow(cp, index);
    index += rows.length;
    return rows;
  });
}



/**
 * Returns a stable key for a party+attorney combination for merging draft and case rows.
 * Draft (CaseParticipantJSON) rows with the same key override CaseParticipantInfo-derived rows.
 */
export function getPartyRowMergeKey(row: PartyRow): string {
  const partyKey = String(row.partyId ?? row.tempPartyId ?? '');
  const attorneyKey = `${row.orgId ?? ''}|${row.attorneyId ?? row.attorney?.fullName ?? ''}`;
  return `${partyKey}|${attorneyKey}`;
}

/**
 * Merges draft-derived rows with case-API-derived rows so there is one row per party+attorney.
 * When the same party+attorney exists in both, the draft row wins (CaseParticipantJSON overrides CaseParticipantInfo).
 *
 * @param draftRows - Rows from draft filing caseParticipants (expanded by attorney).
 * @param caseRows - Rows from case participants API (one per representation).
 * @returns Merged rows with indices reassigned.
 */
export function mergePartyRowsDraftOverCase(draftRows: PartyRow[], caseRows: PartyRow[]): PartyRow[] {
  const draftKeys = new Set(draftRows.map(getPartyRowMergeKey));
  const caseOnlyRows = caseRows.filter((r) => !draftKeys.has(getPartyRowMergeKey(r)));
  const merged = [...draftRows, ...caseOnlyRows];
  return merged.map((row, i) => ({ ...row, index: i }));
}

/**
 * Maps a single sending-parties grid row (one party+representation) to ParticipantRepresentation.
 *
 * @param party - The grid row (one representation).
 * @returns A ParticipantRepresentation for the representations array.
 */
export function reviewFilingPartyToRepresentation(party: SendingPartiesPartyRow): ParticipantRepresentation {
  const attorneyName =
    party.attorney?.fullName ??
    ([party.attorney?.firstName, party.attorney?.middleName, party.attorney?.lastName].filter(Boolean).join(' ').trim() || party.attorney?.lastName);
  return {
    id: party.attorneyId,
    name: attorneyName ?? undefined,
    typeId: party.attorneyTypeId,
    type: party.attorneyType ?? undefined,
    organizationName: party.firm,
    barNumber: party.attorneyBarNumber ?? undefined,
    // Counsel type is separate from attorney type on the grid row; attach for display per representation
    ...(party.counselType != null && { counselType: party.counselType }),
    ...(party.counselTypeId != null && { counselTypeId: party.counselTypeId }),
  } as ParticipantRepresentation;
}

/**
 * Maps sending-parties grid rows to PartyRows unique by party id, with multiple representations.
 * API returns one row per party+representation; this groups by partyId and builds one PartyRow
 * per party with a representations array containing all representations.
 *
 * @param parties - One row per party+representation from the API or mock.
 * @returns Party rows with indices and representations arrays.
 */
export function reviewFilingPartiesToPartyRows(parties: SendingPartiesPartyRow[]): PartyRow[] {
  const byPartyId = new Map<number, SendingPartiesPartyRow[]>();
  for (const p of parties) {
    const id = p.partyId;
    if (id == null) continue;
    const group = byPartyId.get(id);
    if (group) {
      group.push(p);
    } else {
      byPartyId.set(id, [p]);
    }
  }
  let index = 0;
  return Array.from(byPartyId.entries()).map(([partyId, group]) => {
    const first = group[0];
    const representations: ParticipantRepresentation[] = group
      .map(reviewFilingPartyToRepresentation)
      .sort((a, b) => {
        const aName = (a.name ?? '').toLowerCase();
        const bName = (b.name ?? '').toLowerCase();
        if (aName < bName) return -1;
        if (aName > bName) return 1;
        return 0;
      });
    const primaryRep = representations[0];
    const row: PartyRow = {
      fsxTableRowStatus: FsxTableRowStatus.None,
      index: index++,
      partyId,
      partyName: first.partyName,
      ssn: first.ssn,
      dateOfBirth: first.dateOfBirth,
      partyType: first.partyType,
      partyTypeId: first.partyTypeId,
      confidential: first.confidential,
      entityTypeId: first.entityTypeId,
      addresses: first.addresses,
      email: first.email,
      phoneNumber: first.phoneNumber,
      extension: first.extension,
      aliases: first.aliases,
      attorneyBarNumber: first.attorneyBarNumber ?? undefined,
      representations,
    };
    if (primaryRep) {
      row.attorney = toPersonName(primaryRep.name ?? undefined);
      row.attorneyId = primaryRep.id;
      row.attorneyType = primaryRep.type ?? undefined;
      row.attorneyTypeId = primaryRep.typeId;
      row.counselType = first.counselType ?? undefined;
      row.counselTypeId = first.counselTypeId ?? undefined;
      row.firm = primaryRep.organizationName;
      row.orgId = primaryRep.organizationId;
    }
    return row;
  });
}

/**
 * Maps a single case participant and one representation to a party row (one row per representation).
 *
 * @param participant The case participant info.
 * @param representation The representation for this row, or undefined for party-only.
 * @param index The index of the party row.
 * @returns A party row.
 */
export function mapCaseParticipantInfoToPartyRowForRepresentation(
  participant: CaseParticipantInfo,
  representation: ParticipantRepresentation | undefined,
  index: number
): PartyRow {
  const row: PartyRow = {
    partyId: participant.partyId,
    partyName: toPersonName(participant.partyName ?? undefined),
    partyTypeId: participant.partyTypeId,
    partyType: participant.partyType ?? undefined,
    fsxTableRowStatus: FsxTableRowStatus.None,
    index,
  };
  if (representation) {
    row.attorney = toPersonName(representation.name ?? undefined);
    row.attorneyId = representation.id;
    row.counselType = representation.type ?? undefined;
    row.counselTypeId = representation.typeId;
    row.firm = representation.organizationName ?? undefined;
    row.attorneyType = representation.type ?? undefined;
    row.orgId = representation.organizationId ?? undefined;
  }
  return row;
}

/**
 * Maps a single case participant to a party row (uses first representation only).
 *
 * @param participant The case participant info.
 * @param index The index of the party row.
 * @returns A party row.
 */
export function mapCaseParticipantInfoToPartyRow(
  participant: CaseParticipantInfo,
  index: number
): PartyRow {
  return mapCaseParticipantInfoToPartyRowForRepresentation(participant, participant.representation?.[0], index);
}

/**
 * Maps an array of case participants to party rows for the parties table.
 *
 * @param participants The case participant infos.
 * @returns Party rows.
 */
export function caseParticipantInfosToPartyRows(
  participants: CaseParticipantInfo[]
): PartyRow[] {
  return participants.map(mapCaseParticipantInfoToPartyRow);
}

/**
 * Options for expanding case participants to one row per representation.
 */
export interface CaseParticipantInfosToRowsByRepresentationOptions {
  /** If set, only representations that pass this filter produce a row. */
  filterRep?: (rep: ParticipantRepresentation) => boolean;
  /** When a participant has no (matching) representations: 'omit' = no row, 'partyOnly' = one row with party info only. */
  emptyRepsBehavior?: 'omit' | 'partyOnly';
}

/**
 * Expands case participants to party rows, one row per representation.
 * Use for "My Firm" (filter by org + omit when no match) or "Other" (all reps + party-only when none).
 *
 * @param participants Case participant infos.
 * @param options Optional filter and behavior when participant has no matching representations.
 * @returns Party rows.
 */
export function caseParticipantInfosToPartyRowsByRepresentation(
  participants: CaseParticipantInfo[],
  options?: CaseParticipantInfosToRowsByRepresentationOptions
): PartyRow[] {
  const filterRep = options?.filterRep;
  const emptyRepsBehavior = options?.emptyRepsBehavior ?? (filterRep ? 'omit' : 'partyOnly');
  let rowIndex = 0;
  return participants.flatMap((participant) => {
    const reps = participant.representation ?? [];
    const filtered = filterRep ? reps.filter(filterRep) : reps;
    if (filtered.length === 0) {
      if (emptyRepsBehavior === 'partyOnly') {
        return [mapCaseParticipantInfoToPartyRowForRepresentation(participant, undefined, rowIndex++)];
      }
      return [];
    }
    return filtered.map((rep) =>
      mapCaseParticipantInfoToPartyRowForRepresentation(participant, rep, rowIndex++)
    );
  });
}

/** Placeholder representation name from the API; replace with draft when present. */
export const NO_ANSWER_ON_FILE = 'No Answer on File';

function isPlaceholderRepresentation(row: PartyRow): boolean {
  const name = row.attorney?.fullName?.trim() ?? row.attorney?.lastName?.trim() ?? '';
  return name.toLowerCase() === NO_ANSWER_ON_FILE.toLowerCase();
}

